import { Box, CircularProgress, Modal, Typography } from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";
import dayjs from "dayjs";
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  mapInsuranceVerificationFilterField,
  mapInsuranceVerificationSortField,
} from "../../../api/filters/insuranceVerificationFilters";
import useQueryClient from "../../../api/query";
import type { PREDEFINED_INSURANCE_VERIFICATION_FILTERS } from "../../../status";
import type { BackendFilters } from "../../../utils/backendFilters";
import useDevMode from "../../../utils/devmode";
import DataTable, {
  type PaginationModel,
  type SortModel,
  useServerFilterModel,
} from "../../DataTable";
import CustomNoRowsOverlay from "../../TableNoDataOverlay";
import { getDefaultSortModel } from "../store";
import { getDefaultColumns, getDefaultColumnVisibility } from "./columns";

interface InsuranceVerificationTableProps {
  isLoading: boolean;
  verifications: InsuranceVerificationMessage[];
  updateVerification: (payload: InsuranceVerificationUpdateMessage) => void;
  postToPMS: (verification: InsuranceVerificationMessage) => void;
  unpost: (verification: InsuranceVerificationMessage) => void;
  markManuallyPosted?: (verification: InsuranceVerificationMessage) => void;
  rowCount?: number;
  persistKey?: string;
  selectedFilter: PREDEFINED_INSURANCE_VERIFICATION_FILTERS;
  selectedPractices?: PracticeMessage[];
  onFilterChange?: (filters: BackendFilters) => void;
  onSortChange?: (field: string, direction: "asc" | "desc") => void;
  page: number;
  pageSize: number;
  onPageChange?: (page: number, pageSize: number) => void;
  getRowClassName?: (row: InsuranceVerificationMessage) => string;
  sx?: SxProps<Theme>;
}

export default function InsuranceVerificationTable({
  isLoading,
  verifications,
  updateVerification,
  postToPMS,
  unpost,
  markManuallyPosted,
  rowCount,
  persistKey = "insuranceVerificationTable",
  selectedFilter,
  selectedPractices = [],
  onFilterChange,
  onSortChange,
  page,
  pageSize,
  onPageChange,
  getRowClassName,
  sx,
}: InsuranceVerificationTableProps) {
  const navigate = useNavigate();
  const [sortModel, setSortModel] = useState<SortModel>(() =>
    getDefaultSortModel(selectedFilter),
  );
  const [screenshotsModalOpen, setScreenshotsModalOpen] = useState(false);
  const [screenshots, setScreenshots] = useState<string[]>([]);
  const [loadingScreenshots, setLoadingScreenshots] = useState(false);
  const queryClient = useQueryClient();
  const isDevMode = useDevMode();

  const { filterModel, onFilterModelChange } = useServerFilterModel({
    persistKey,
    mapField: mapInsuranceVerificationFilterField,
    onFiltersChange: onFilterChange,
  });

  useEffect(() => {
    setSortModel(getDefaultSortModel(selectedFilter));
  }, [selectedFilter]);

  const fetchScreenshots = async (
    verification: InsuranceVerificationMessage,
  ) => {
    setLoadingScreenshots(true);
    try {
      const fetchedShots =
        await queryClient.getInsuranceVerificationScreenshots(
          verification.wieldyId,
        );
      setScreenshots(fetchedShots);
      setScreenshotsModalOpen(true);
    } catch (error) {
      // biome-ignore lint/suspicious/noConsole: intentional browser console output
      console.error(error);
    } finally {
      setLoadingScreenshots(false);
    }
  };

  const fetchBennyScreenshots = async (
    verification: InsuranceVerificationMessage,
  ) => {
    setLoadingScreenshots(true);
    try {
      const fetchedShots = await queryClient.getBennyScreenshots(
        verification.wieldyId,
      );
      setScreenshots(fetchedShots);
      setScreenshotsModalOpen(true);
    } catch (error) {
      // biome-ignore lint/suspicious/noConsole: intentional browser console output
      console.error(error);
    } finally {
      setLoadingScreenshots(false);
    }
  };

  // The search endpoint sorts by one field, so only the primary key is sent.
  const handleSortModelChange = useCallback(
    (model: SortModel) => {
      setSortModel(model);
      if (onSortChange && model.length > 0) {
        const { field, sort } = model[0];
        onSortChange(mapInsuranceVerificationSortField(field), sort);
      }
    },
    [onSortChange],
  );

  const handlePaginationModelChange = useCallback(
    (model: PaginationModel) => onPageChange?.(model.page + 1, model.pageSize),
    [onPageChange],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: adding unpost would change when this runs
  const columns = React.useMemo(
    () =>
      getDefaultColumns(
        updateVerification,
        postToPMS,
        unpost,
        markManuallyPosted,
        navigate,
        selectedPractices,
        isDevMode,
        fetchScreenshots,
        fetchBennyScreenshots,
      ),
    [
      updateVerification,
      postToPMS,
      markManuallyPosted,
      navigate,
      selectedPractices,
      isDevMode,
      // biome-ignore lint/correctness/useExhaustiveDependencies: fetchScreenshots is recreated every render; fixing changes when this runs
      fetchScreenshots,
      // biome-ignore lint/correctness/useExhaustiveDependencies: fetchBennyScreenshots is recreated every render; fixing changes when this runs
      fetchBennyScreenshots,
    ],
  );

  return (
    <>
      <DataTable
        loading={isLoading}
        persistKey={persistKey}
        rows={verifications}
        getRowId={(row) => row.wieldyId}
        columns={columns}
        rowCount={rowCount ?? verifications.length}
        paginationModel={{ page: page - 1, pageSize }}
        pageSizeOptions={[10, 20, 40]}
        onPaginationModelChange={handlePaginationModelChange}
        filterModel={filterModel}
        onFilterModelChange={onFilterModelChange}
        sortModel={sortModel}
        onSortModelChange={handleSortModelChange}
        initialColumnVisibility={getDefaultColumnVisibility(isDevMode)}
        exportFileName={`insurance_verifications_${dayjs().format("YYYY-MM-DD")}`}
        noRowsOverlay={<CustomNoRowsOverlay />}
        getRowClassName={getRowClassName}
        sx={sx}
      />
      <Modal
        open={screenshotsModalOpen}
        onClose={() => setScreenshotsModalOpen(false)}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "background.paper",
            boxShadow: 24,
            padding: 4,
            width: "70%",
            maxWidth: "90%",
            maxHeight: "90%",
            overflow: "auto",
            borderRadius: 3,
          }}
        >
          {loadingScreenshots && <CircularProgress />}
          {!loadingScreenshots && !screenshots.length && (
            <Typography>No screenshots found.</Typography>
          )}
          {!loadingScreenshots &&
            screenshots.length > 0 &&
            screenshots.map((screenshot, index) => (
              <Box key={screenshot} sx={{ mb: 3 }}>
                <img
                  src={screenshot}
                  alt={`Screenshot ${index + 1}`}
                  style={{ maxWidth: "100%", marginBottom: "1rem" }}
                />
              </Box>
            ))}
        </Box>
      </Modal>
    </>
  );
}
